import { useEffect, useRef } from 'react';

const rows = [
  {
    feature: '방수 기능',
    icon: 'ri-drop-fill',
    smart: '패킹 일체형',
    existing: '없음',
  },
  {
    feature: '시공 속도',
    icon: 'ri-timer-flash-fill',
    smart: '30~40% 단축',
    existing: '느림',
  },
  {
    feature: '방수 내구성',
    icon: 'ri-shield-star-fill',
    smart: '5년 이상',
    existing: '1~2년',
  },
  {
    feature: '설치 난이도',
    icon: 'ri-tools-fill',
    smart: '초보자 OK',
    existing: '숙련자 의존',
  },
  {
    feature: 'A/S 발생',
    icon: 'ri-customer-service-2-fill',
    smart: '대폭 감소',
    existing: '잦음',
  },
  {
    feature: '별도 실리콘 작업',
    icon: 'ri-brush-fill',
    smart: '불필요',
    existing: '필수',
  },
];

const ComparisonSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('opacity-100', 'translate-y-0');
        });
      },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    rowRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="comparison" className="py-24 bg-[#F8F9FC]">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">

        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="text-center mb-14 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Comparison</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
            기존 마감캡과 무엇이 다른가요?
          </h2>
          <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto">
            6가지 핵심 항목으로 한눈에 비교해보세요
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        {/* 헤더 라벨 */}
        <div className="grid grid-cols-3 mb-3 px-2">
          <div />
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 bg-[#FF6B35] text-white text-xs font-bold px-4 py-1.5 rounded-full">
              <i className="ri-shield-check-fill" />
              스마트캡
              <span className="bg-white/25 text-white text-[10px] px-1.5 py-0.5 rounded-full ml-1">추천</span>
            </span>
          </div>
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 bg-gray-300 text-gray-600 text-xs font-semibold px-4 py-1.5 rounded-full">
              <i className="ri-contrast-2-fill" />
              기존 마감캡
            </span>
          </div>
        </div>

        {/* 비교 테이블 */}
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
          {rows.map((row, i) => (
            <div
              key={i}
              ref={(el) => { rowRefs.current[i] = el; }}
              className={`grid grid-cols-3 text-sm border-b border-gray-100 last:border-0
                hover:bg-[#FF6B35]/3 transition-colors duration-200
                opacity-0 translate-y-4 transition-[opacity,transform] duration-500
                ${i % 2 === 1 ? 'bg-[#FAFBFC]' : 'bg-white'}`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              {/* 항목명 */}
              <div className="px-5 py-4 flex items-center gap-2.5">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#1B2A4A]/6 flex-shrink-0">
                  <i className={`${row.icon} text-sm text-[#1B2A4A]`} />
                </div>
                <span className="font-semibold text-[#1B2A4A] text-sm leading-snug">{row.feature}</span>
              </div>

              {/* 스마트캡 */}
              <div className="px-5 py-4 flex items-center justify-center border-l border-gray-100 bg-[#FF6B35]/4">
                <span className="inline-flex items-center gap-1.5 text-[#1B2A4A] font-bold text-sm">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#FF6B35] flex-shrink-0">
                    <i className="ri-check-line text-white text-xs" />
                  </span>
                  {row.smart}
                </span>
              </div>

              {/* 기존 마감캡 */}
              <div className="px-5 py-4 flex items-center justify-center border-l border-gray-100">
                <span className="inline-flex items-center gap-1.5 text-gray-400 text-sm">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 flex-shrink-0">
                    <i className="ri-close-line text-gray-400 text-xs" />
                  </span>
                  {row.existing}
                </span>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-xs mt-5">
          ※ 위 비교는 일반적인 현장 사용 조건을 기준으로 작성되었습니다.
        </p>
      </div>
    </section>
  );
};

export default ComparisonSection;
